import { Button, Card, CardBody, Stack } from '@chakra-ui/react';
import { DocumentType, NotificationCategory, TimeOfDay } from '@pelicargo/types';
import { useError } from '@pelicargo/ui';
import { removeDash } from '@pelicargo/utils';
import { subDays } from 'date-fns';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useBookingState, useBookingStateSetter } from '../../hooks/useBookingState';
import { useConditionalResolver } from '../../hooks/useConditionalResolver';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { DefaultContactsCard } from '../DefaultContactsCard';
import { FlightJourneyCard } from '../FlightJourneyCard';
import { AirlineBookingForm } from '../Forms/AirlineBookingForm';
import { QuoteVariantTabs } from '../QuoteVariantTabs';
import { RequestDetailsCard } from '../RequestDetailsCard';
import { SimpleForm } from '../SimpleForm';
import { TermsAndConditions } from '../TermsAndConditions';

type Props = {
  quote: TrpcOutputs['getQuote'];
  request: TrpcOutputs['getQuote']['request'];
};

export const AfrBooking = ({ quote, request }: Props) => {
  const { handleError } = useError();
  const navigate = useNavigate();
  const { targetAccount } = useTargetAccount();
  const { selectedQuoteVariantId } = useSelectedQuoteVariant();

  const quoteVariant = useMemo(() => {
    return quote?.quote_variants.find((variant) => variant.id === selectedQuoteVariantId);
  }, [quote?.quote_variants, selectedQuoteVariantId]);

  const { data: requestDefaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    { id: quote?.request_id, updates_for: NotificationCategory.Booking },
    { enabled: !!quote?.request_id },
  );
  const { data: accountDefaultContacts } = trpc.getDefaultContactsByAccount.useQuery(
    { id: targetAccount?.id, updates_for: NotificationCategory.Booking },
    { enabled: !!targetAccount?.id },
  );

  // Unique contacts by contact_id prioritizing request contacts
  const default_contacts = useMemo(
    () => uniqBy([...(requestDefaultContacts || []), ...(accountDefaultContacts || [])], 'contact_id'),
    [accountDefaultContacts, requestDefaultContacts],
  );

  const baseSchema = z.object({
    awb_number: z.string().superRefine((value, ctx) => {
      const cleanedValue = removeDash(value);
      if (quote?.is_awb_required_to_book && (!value || cleanedValue.length !== 11)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${quote?.provider_id} requires and 11-digit AWB before booking`,
        });
      }

      if (cleanedValue && cleanedValue.length !== 11 && cleanedValue.length !== 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'AWB number must be exactly 11 characters',
        });
      }
    }),
    documents: z
      .array(
        z.object({
          id: z.number(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          original_filename: z.string(),
          type: z.nativeEnum(DocumentType, { invalid_type_error: 'Invalid Document Type' }).nullish(),
          request_id: z.number().nullish(),
          document_id: z.number().nullish(),
        }),
      )
      .optional(),
    terms: z.boolean().refine((value) => value === true, {
      message: 'Please accept the terms and conditions',
    }),
    tender_date: z.coerce
      .date()
      // Coerce defaults to a unix timestamp of 0, so this properly filters it
      .min(subDays(new Date(), 2), 'Tender date is required'),
    tender_date_time_of_day: z.nativeEnum(TimeOfDay).nullish(),
    customer_ref: z.string().optional(),
    is_pre_screened: z.boolean().optional(),
    additional_information: z.string(),
    default_contacts: z.array(
      z.object({
        contact_id: z.string(),
        updates_for: z.array(z.nativeEnum(NotificationCategory)).default([]),
      }),
    ),
  });

  const { fields, schema, resolver } = useConditionalResolver(baseSchema, quote?.id);

  type FormValues = z.infer<typeof schema>;

  const state = useBookingState(quote);
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
    defaultValues: {
      ...state,
      tender_date: quoteVariant?.flight_path?.[0]?.departs_at ?? state?.tender_date,
      tender_date_time_of_day: quote?.tender_date_time_of_day ?? TimeOfDay.EVENING,
      hs_code: request?.hs_code,
      commodity: request.commodity,
      is_military_related: request.is_military_related ?? false,
      is_consolidated: request?.is_consolidated ?? false,
    },
  });
  useBookingStateSetter(quote, methods);
  const { setValue } = methods;

  useEffect(() => {
    const request = quote?.request;
    const customer_ref = request?.customer_ref;
    const awb_number = quote?.awb_number || quote?.provider?.awb_prefix;
    const documents = request?.documents;

    methods.reset({
      ...methods.getValues(),
      customer_ref: customer_ref ?? '',
      awb_number: awb_number ?? '',
      documents: documents ?? [],
      is_pre_screened: false,
      ...state,
      tender_date: quoteVariant?.flight_path?.[0]?.departs_at ?? quote?.tender_date,
    });
  }, [
    methods,
    quote?.provider?.awb_prefix,
    quote?.awb_number,
    quote?.chargeable_weight,
    quote?.request,
    quote?.tender_date,
    quoteVariant?.flight_path,
    state,
  ]);

  useEffect(() => {
    if (quote.provider.has_api_booking) {
      const assumedTenderDate = quoteVariant?.flight_path?.[0]?.departs_at;
      setValue('tender_date', assumedTenderDate);
    }
  }, [quote.provider.has_api_booking, quoteVariant?.flight_path, setValue]);

  useEffect(() => {
    if (default_contacts?.length) {
      const contacts = default_contacts.map((contact) => ({
        contact_id: String(contact.contact_id),
        updates_for: contact.updates_for,
      }));
      methods.setValue(
        'default_contacts',
        contacts.map((contact) => ({
          contact_id: contact.contact_id,
          updates_for: contact.updates_for as NotificationCategory[],
        })),
      );
    }
  }, [default_contacts, default_contacts?.length, methods]);

  const bookQuoteVariant = trpc.bookQuoteVariant.useMutation();

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (!quoteVariant) {
          handleError('Please select a quote variant.');
          return;
        }

        const normalizedAwb = values.awb_number?.length > 3 ? removeDash(values.awb_number) : null;

        const requestId = quote?.request?.id;
        const quoteId = quote?.id;

        await bookQuoteVariant.mutateAsync({
          id: quoteVariant?.id,

          tenderDate: values.tender_date,
          tenderDateTimeOfDay: values.tender_date_time_of_day as TimeOfDay,
          isPreScreened: values.is_pre_screened,
          awbNumber: normalizedAwb,
          additionalInformation: values.additional_information,
          customerRef: values.customer_ref,

          defaultContacts: values.default_contacts.map((contact) => ({
            contact_id: Number(contact.contact_id),
            updates_for: contact?.updates_for || [],
          })),

          hs_code: values.hs_code,
          emergency_contact: values.emergency_contact,
          is_consolidated: values.is_consolidated,
          commodity: values.commodity,
          is_military_related: values.is_military_related,
        });

        navigate(`/requests/${requestId}/quotes/${quoteId}/booked`);
      } catch (error) {
        handleError(error);
      }
    },
    [bookQuoteVariant, handleError, navigate, quote?.id, quote?.request?.id, quoteVariant],
  );

  return (
    <SimpleForm onSubmit={handleSubmit} defaultValues={{ default_contacts }} {...(methods as any)}>
      <Stack direction={{ base: 'column', lg: 'row' }} w="full" spacing="6">
        <Stack h="fit-content" w="full">
          <Card h="auto" px="4" py="3">
            <CardBody p="0">
              <Stack spacing="6">
                <AirlineBookingForm quote={quote} conditionalFields={fields} />
                <DefaultContactsCard notificationCategory={NotificationCategory.Booking} />
              </Stack>
            </CardBody>
          </Card>
        </Stack>
        <Stack w="full" spacing="6">
          <Card h="auto">
            <CardBody p="0">
              <QuoteVariantTabs
                quote={quote}
                quoteVariants={quote?.quote_variants || []}
                showBookingLink={false}
                navigateOnSelection
              />
              <Stack p="4">
                <TermsAndConditions provider={quote?.provider} />
                <Button
                  colorScheme="primary"
                  size="lg"
                  onClick={methods.handleSubmit(handleSubmit)}
                  isLoading={bookQuoteVariant?.isLoading}
                >
                  Request Booking
                </Button>
              </Stack>
            </CardBody>
          </Card>
          <FlightJourneyCard showHeader quote={quote} quoteVariant={quoteVariant} />
          <RequestDetailsCard request={request} quote={quote} />
        </Stack>
      </Stack>
    </SimpleForm>
  );
};
